import { ArrowRight, Search, X } from 'lucide-react';
import { Link } from 'react-router';

/**
 * Searching Admin → Server settings (#630). Each option is listed here with the section (or fold) it lives in and a
 * few extra words people search for; a result opens that place and flashes the option. Options on other Admin tabs
 * are listed too, as links.
 */

/** The sections, in page order, with the group heading they sit under. */
const SECTIONS: Record<string, { title: string; group: string }> = {
  's-site': { title: 'Site', group: 'Site' },
  's-accounts': { title: 'Sign-up and accounts', group: 'Families and accounts' },
  's-security': { title: 'Security', group: 'Families and accounts' },
  's-api': { title: 'API access', group: 'Families and accounts' },
  's-features': { title: 'Features', group: 'Families and accounts' },
  's-retention': { title: 'Data retention', group: 'Families and accounts' },
  's-agent': { title: 'Agent (Windows and Mac)', group: 'The child’s computer' },
  's-browser-extension-auto-i': { title: 'Browser extension', group: 'The child’s computer' },
  's-live-view': { title: 'Live view and cameras', group: 'The child’s computer' },
  's-portfolio': { title: 'Work samples and portfolio', group: 'Homeschool records' },
  's-records': { title: 'Records', group: 'Homeschool records' },
  's-evaluator-links': { title: 'Evaluator links', group: 'Homeschool records' },
  's-email-smtp': { title: 'Email (SMTP)', group: 'Email and messages' },
  's-weekly-summary': { title: 'Weekly summary email', group: 'Email and messages' },
  's-admin-notifications': { title: 'Admin notifications', group: 'Email and messages' },
  's-payments': { title: 'Payments (Stripe)', group: 'Business' },
  's-signup-codes': { title: 'Sign-up codes', group: 'Business' },
  's-billing-notices': { title: 'Billing notices', group: 'Business' },
  's-support': { title: 'Help & Feedback', group: 'Business' },
  's-terms': { title: 'Terms and privacy', group: 'Business' },
  's-server': { title: 'Server', group: 'Server' },
};

/** [section, where to open (the section or a fold in it), the option's name, extra words]. */
const OPTIONS: [string, string, string, string?][] = [
  ['s-site', 's-site', 'Public address', 'url domain link reverse proxy'],
  ['s-site', 'site-theme', 'Default theme', 'appearance color aurora dark light'],
  ['s-site', 'site-css', 'Custom styling (CSS)', 'css style colors look branding'],
  ['s-accounts', 's-accounts', 'Allow new parents to sign up', 'registration signups private server open closed'],
  ['s-accounts', 's-accounts', 'Get started checklist on Home', 'onboarding new families steps'],
  ['s-accounts', 'accounts-admins', 'Admins', 'administrator admin emails'],
  ['s-security', 's-security', 'Require two-factor sign-in', '2fa mfa totp authenticator'],
  ['s-security', 's-security', 'Passkeys', 'webauthn face id touch id security key'],
  ['s-security', 's-security', 'Failed sign-ins and lockout', 'brute force cooldown lock password attempts'],
  ['s-security', 's-security', 'Sign-in report', 'failed sign-ins lockouts audit'],
  ['s-security', 's-security', 'Allow webhooks to reach private/LAN addresses', 'webhook home assistant local network'],
  ['s-api', 's-api', 'API access for each family', 'rest api keys webhooks enable disable integrations'],
  ['s-features', 's-features', 'Features off for everyone', 'disable feature server-wide kill switch'],
  ['s-features', 'features-new-children', 'Features new children start hidden', 'default hidden features child'],
  ['s-retention', 's-retention', 'Keep activity history (days)', 'retention activity delete purge'],
  ['s-retention', 's-retention', 'Keep sign-in history (days)', 'retention sign-ins audit'],
  ['s-retention', 's-retention', 'Days before a requested account deletion', 'retention delete account gdpr erase'],
  ['s-retention', 's-retention', 'Keep work samples for (years)', 'retention portfolio samples files'],
  ['s-agent', 's-agent', 'Shrink the School Resources button after (seconds)', 'launcher floating button collapse'],
  ['s-agent', 's-agent', 'Check for updates every (hours)', 'auto update agent version windows mac'],
  ['s-agent', 's-agent', 'Alert a parent when the app goes offline during school time', 'offline alert grace seconds monitoring'],
  ['s-agent', 's-agent', 'One-click Student Portal sign-in', 'student portal open button tray menu auto sign in link'],
  ['s-browser-extension-auto-i', 's-browser-extension-auto-i', 'Chrome and Edge extension ids', 'browser extension auto-install store web store add-ons'],
  ['s-browser-extension-auto-i', 's-browser-extension-auto-i', 'School site readers', 'connexus pearson planner grades import read school site'],
  ['s-browser-extension-auto-i', 's-browser-extension-auto-i', 'Read the school site in the background', 'connexus interval hours background update'],
  ['s-browser-extension-auto-i', 's-browser-extension-auto-i', 'Connexus attendance bookmark', 'connexus attendance learning coach bookmark send'],
  ['s-browser-extension-auto-i', 's-browser-extension-auto-i', 'Extension package for the stores', 'download zip upload submit update version chrome edge'],
  ['s-live-view', 'live-turn', 'STUN and TURN servers', 'webrtc relay coturn ice live view connection'],
  ['s-live-view', 'live-blur', 'Camera blur', 'blur background camera privacy'],
  ['s-portfolio', 's-portfolio', 'Work samples and the portfolio PDF', 'portfolio samples photos pdf'],
  ['s-portfolio', 's-portfolio', 'Children can add their work when they turn it in', 'student uploads turn in'],
  ['s-portfolio', 's-portfolio', 'Largest file and space per family (MB)', 'storage size limit quota mb'],
  ['s-portfolio', 's-portfolio', 'Biggest document a family can upload (MB)', 'documents upload size limit'],
  ['s-records', 's-records', 'Tests and evaluations', 'standardized test scores evaluation'],
  ['s-records', 's-records', 'Field trips: photos per trip, most hours a trip can count', 'field trip outing hours'],
  ['s-records', 's-records', 'High school transcript: honors and AP bonus, credits', 'gpa transcript honors ap dual enrollment credits'],
  ['s-records', 's-records', 'Homeschool documents', 'paperwork notices diploma files'],
  ['s-records', 's-records', 'Close out the school year', 'archive lock year end'],
  ['s-evaluator-links', 's-evaluator-links', 'Evaluator links: how long and how many', 'share evaluator read-only link'],
  ['s-email-smtp', 's-email-smtp', 'SMTP server, port, security, username, password', 'email mail smtp resend send as test email'],
  ['s-email-smtp', 's-email-smtp', 'Email usage', 'emails sent count'],
  ['s-weekly-summary', 's-weekly-summary', 'Weekly summary email: day, time and who gets it', 'weekly digest summary email'],
  ['s-admin-notifications', 's-admin-notifications', 'Email admins when a family signs up or feedback arrives', 'notifications admin alerts platform'],
  ['s-payments', 's-payments', 'Stripe keys and webhook', 'payments billing stripe subscription'],
  ['s-signup-codes', 's-signup-codes', 'Sign-up codes: discount and Founder codes', 'signup sign-up code coupon promo discount founder percent off'],
  ['s-billing-notices', 's-billing-notices', 'Billing notices: trial, renewal, receipt, cancellation emails by state', 'billing notices emails trial renewal reminder receipt failed payment cancellation state law auto renewal'],
  ['s-support', 's-support', 'Help & Feedback: contact, request types and labels', 'support feedback contact us'],
  ['s-support', 'support-github', 'GitHub (feedback → issues)', 'github app token issues repository'],
  ['s-terms', 's-terms', 'Terms and privacy: version and re-accept', 'legal terms of service privacy policy'],
  ['s-server', 's-server', 'Listen address, port and reverse proxy', 'restart host port https proxy caddy nginx'],
  ['s-server', 's-server', 'Set by the container', 'docker data folder encryption key puid'],
];

/** Settings that live on other Admin tabs. */
const ELSEWHERE: [string, string, string][] = [
  ['/admin/compliance', 'Compliance', 'State requirements, deadline reminders and state documents'],
  ['/admin/plans', 'Plans', 'Plans, prices, free trials and add-ons'],
  ['/admin/growth', 'Growth', 'Signup funnel settings'],
  ['/admin/roles', 'Roles', 'Co-parent roles and permissions'],
];

const norm = (s: string) => s.toLowerCase().replace(/[’']/g, '');

export function SettingsSearch({ query, onQuery, onJump }: { query: string; onQuery: (q: string) => void; onJump: (target: string, label: string) => void }) {
  const q = norm(query.trim());
  const words = q.split(/\s+/).filter(Boolean);
  const matches = (text: string) => words.every((w) => norm(text).includes(w));
  // Skip any option whose section isn't in SECTIONS, so a stray entry can never blank the whole page (#938).
  const hits = q ? OPTIONS.filter(([sec, , label, extra]) => SECTIONS[sec] && matches(`${label} ${extra ?? ''} ${SECTIONS[sec]!.title}`)) : [];
  const bySection = [...new Set(hits.map((h) => h[0]))].map((sec) => ({ sec, items: hits.filter((h) => h[0] === sec) }));
  const others = q ? ELSEWHERE.filter(([, tab, what]) => matches(`${tab} ${what}`)) : [];
  // The first word found in the option's name, to highlight.
  const mark = (label: string) => {
    const w = words.find((x) => norm(label).includes(x));
    const i = w ? norm(label).indexOf(w) : -1;
    if (!w || i < 0) return label;
    return (
      <>
        {label.slice(0, i)}
        <mark>{label.slice(i, i + w.length)}</mark>
        {label.slice(i + w.length)}
      </>
    );
  };
  return (
    <div className="stack settings-search">
      <label className="settings-search-box">
        <Search size={16} aria-hidden />
        <input value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search settings (e.g. two-factor, history, TURN, trip)" aria-label="Search settings" />
        {query && (
          <button type="button" className="icon-btn" aria-label="Clear the search" onClick={() => onQuery('')}>
            <X size={15} />
          </button>
        )}
      </label>
      {q && (
        <>
          <span className="muted small">{hits.length ? `${hits.length} setting${hits.length === 1 ? '' : 's'}` : 'No settings match.'}</span>
          {bySection.map(({ sec, items }) => (
            <div key={sec} className="card stack search-result">
              <span className="search-where">
                {SECTIONS[sec]!.group} › {SECTIONS[sec]!.title}
              </span>
              {items.map(([, target, label]) => (
                <button key={label} type="button" className="search-hit" onClick={() => onJump(target, label)}>
                  <span className="grow">{mark(label)}</span>
                  <ArrowRight size={15} aria-hidden />
                </button>
              ))}
            </div>
          ))}
          {others.map(([to, tab, what]) => (
            <Link key={to} to={to} className="search-elsewhere">
              On another tab: <strong>Admin → {tab}</strong> · {what} <ArrowRight size={14} aria-hidden />
            </Link>
          ))}
        </>
      )}
    </div>
  );
}

/** Old section links (before #630) and where they live now. */
export const OLD_ANCHORS: Record<string, string> = {
  's-general': 's-site',
  's-appearance': 'site-theme',
  's-custom-css': 'site-css',
  's-signin-security': 's-security',
  's-agent-setup': 's-agent',
  's-monitoring': 's-agent',
  's-live-view-connections': 's-live-view',
  's-github-feedback-issues': 'support-github',
  's-platform-notifications': 's-admin-notifications',
  's-set-by-the-container': 'server-container',
};
